import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Download, Plus, Trash2, CheckSquare, Square, Loader2, FileSpreadsheet, ArrowLeft, GripVertical, LayoutGrid, List, ExternalLink, Copy, FileDown } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import M45NavPills from '../components/M45NavPills';

const API = process.env.REACT_APP_BACKEND_URL;
const enconOrange = '#ee7203';
const enconLightGrey = '#f8f8f8';
const getToken = () => localStorage.getItem('token');

const CRM_TO_MEETING_MAP = {
  'Project Name': 'Project Name', 'Account Name': 'Contractor',
  'Grand Total': 'Potential Value to Encon', 'Grand Total (£)': 'Potential Value to Encon', 'Quote Stage': 'Lead / Source',
  'Record Id': 'Record ID', 'Record ID': 'Record ID',
  'Subject': 'Address/Project Address',
  'Sub Contractor Package': 'Sub Contractor',
  'Date Quote Raised': 'Start Date', 'Follow Up Date': 'End Date',
  'Created By': 'Lead / Source',
};

const fieldBoxClass = 'bg-gray-200/70 rounded-lg px-2.5 py-2 cursor-pointer hover:bg-gray-200 transition-colors shadow-inner';
const fieldLabelClass = 'text-[10px] font-semibold text-gray-400 uppercase tracking-wider pointer-events-none';
const fieldTextClass = 'text-sm text-gray-800';

const SDPCRMPage = ({ user, onLogout }) => {
  const navigate = useNavigate();

  // Active tab: string key
  const [activeTab, setActiveTab] = useState('rockwool');

  // Dynamic SDP tabs - each has {key, label}
  const [sdpTabs, setSdpTabs] = useState([{ key: 'rockwool', label: 'Rockwool' }]);
  const [addingTab, setAddingTab] = useState(false);
  const [newTabName, setNewTabName] = useState('');

  // Per-tab state stored in objects keyed by tab name
  const [tabData, setTabData] = useState({
    rockwool: { loaded: false, fileMode: null, file: null, filename: '', headers: [], rows: [], projects: [], trades: [] },
    'key-projects': { loaded: false, fileMode: null, file: null, filename: '', headers: [], rows: [], projects: [], trades: [] },
  });

  const getTab = () => tabData[activeTab] || { loaded: false, fileMode: null, file: null, filename: '', headers: [], rows: [], projects: [], trades: [] };
  const setTab = (updates) => setTabData(prev => ({ ...prev, [activeTab]: { ...(prev[activeTab] || {}), ...updates } }));

  const addSdpTab = (name) => {
    if (!name.trim()) return;
    const key = name.trim().toLowerCase().replace(/\s+/g, '-');
    if (tabData[key]) { toast.error('Tab already exists'); return; }
    setSdpTabs(prev => [...prev, { key, label: name.trim() }]);
    setTabData(prev => ({ ...prev, [key]: { loaded: false, fileMode: null, file: null, filename: '', headers: [], rows: [], projects: [], trades: [] } }));
    setActiveTab(key);
    setAddingTab(false);
    setNewTabName('');
  };

  const removeSdpTab = (key) => {
    if (sdpTabs.length <= 1) return;
    setSdpTabs(prev => prev.filter(t => t.key !== key));
    setTabData(prev => { const n = { ...prev }; delete n[key]; return n; });
    if (activeTab === key) setActiveTab(sdpTabs[0].key === key ? (sdpTabs[1]?.key || 'key-projects') : sdpTabs[0].key);
  };

  const isKeyProjects = activeTab === 'key-projects';

  // Shared UI state
  const [loadingFile, setLoadingFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [showActionsPrompt, setShowActionsPrompt] = useState(false);
  const [viewMode, setViewMode] = useState('cards');
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [dragging, setDragging] = useState(false);

  const [pdfLoading, setPdfLoading] = useState(false);
  const cardsRef = useRef(null);

  // CRM quotes import modal
  const [crmModalOpen, setCrmModalOpen] = useState(false);
  const [crmHeaders, setCrmHeaders] = useState([]);
  const [crmRows, setCrmRows] = useState([]);
  const [crmSelected, setCrmSelected] = useState({});
  const [loadingCrm, setLoadingCrm] = useState(false);
  const [crmDragging, setCrmDragging] = useState(false);

  // (Add-to-CRM opens the Zoho page in a new tab; iframe embedding is blocked by Zoho's X-Frame-Options.)

  const fileInputRef = useRef(null);
  const crmInputRef = useRef(null);

  const tab = getTab();

  // ===== AUTO-SAVE & LOAD STATE =====
  const [stateLoaded, setStateLoaded] = useState(false);
  const saveTimer = useRef(null);

  // Load state on mount
  useEffect(() => {
    const loadState = async () => {
      try {
        const res = await fetch(`${API}/api/sdp-crm/state`, { headers: { Authorization: `Bearer ${getToken()}` } });
        const d = await res.json();
        if (d.success && d.data) {
          const s = d.data;
          if (s.activeTab) setActiveTab(s.activeTab);
          if (s.sdpTabs?.length) setSdpTabs(s.sdpTabs);
          if (s.tabData) {
            // Migrate any legacy {contractor, sub} contractor entries → {role, name, …}
            const migratedTabData = {};
            Object.entries(s.tabData).forEach(([k, t]) => {
              if (t && Array.isArray(t.rows)) {
                migratedTabData[k] = { ...t, rows: t.rows.map(r => ({ ...r, _contractors: _normaliseContractors(r._contractors) })) };
              } else {
                migratedTabData[k] = t;
              }
            });
            setTabData(migratedTabData);
          }
          if (s.viewMode) setViewMode(s.viewMode);
        }
      } catch (e) { /* silent — first load or no saved state */ }
      setStateLoaded(true);
    };
    loadState();
  }, []);

  // Build saveable state (exclude file objects and transient UI state)
  const buildSaveState = useCallback(() => {
    const cleanTabData = {};
    Object.entries(tabData).forEach(([key, val]) => {
      cleanTabData[key] = { ...val, file: null };
    });
    return { activeTab, sdpTabs, tabData: cleanTabData, viewMode };
  }, [activeTab, sdpTabs, tabData, viewMode]);

  // Debounced auto-save — triggers 1.5s after last change
  useEffect(() => {
    if (!stateLoaded) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await fetch(`${API}/api/sdp-crm/state`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ state: buildSaveState() }),
        });
      } catch (e) { /* silent save failure */ }
    }, 1500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [tabData, sdpTabs, activeTab, viewMode, stateLoaded, buildSaveState]);

  // ===== FILE UPLOAD =====
  const handleFileUpload = async (file) => {
    if (!file) return;
    setLoadingFile(true);
    try {
      let sdpOk = false;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tab_key', activeTab);
      try {
        const res = await fetch(`${API}/api/sdp-report/parse-supplier-meeting`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: formData });
        const data = await res.json();
        if (res.ok && data.success) {
          const merged = mergeDuplicateProjects(data.rows);
          setTab({ loaded: true, fileMode: 'sdp', file, filename: data.filename, headers: data.headers, rows: merged });
          toast.success(`SDP: Loaded ${merged.length} projects`);
          if (data.headers.includes('Key Actions for next Meeting') && data.headers.includes('Action Review from Last Meeting')) {
            if (data.rows.some(r => r['Key Actions for next Meeting']?.trim())) setShowActionsPrompt(true);
          }
          sdpOk = true;
        }
      } catch (e) { /* not SDP */ }
      if (sdpOk) return;

      const formData2 = new FormData();
      formData2.append('file', file);
      const res2 = await fetch(`${API}/api/crm-report/parse`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: formData2 });
      const data2 = await res2.json();
      if (res2.ok && data2.success) {
        setTab({ loaded: true, fileMode: 'crm', file, filename: data2.filename, projects: data2.projects, trades: data2.trades || [] });
        toast.success(`CRM: Loaded ${data2.projects.length} projects`);
        return;
      }
      toast.error('Could not detect file type — needs Key Projects or CRM tab');
    } catch (err) { toast.error(err.message || 'Failed to parse file'); }
    finally { setLoadingFile(false); }
  };

  // ===== MERGE DUPLICATES =====
  // Normalise _contractors: every block = one person ({ role, name, crm_task, task_id }).
  // Legacy rows stored as [{contractor, sub}] get split into separate entries.
  const _normaliseContractors = (contractors) => {
    if (!contractors || !contractors.length) return [{ role: 'main', name: '', crm_task: '', task_id: '' }];
    if (contractors[0] && contractors[0].role) return contractors;
    const out = [];
    contractors.forEach((c, idx) => {
      if (idx === 0) {
        out.push({ role: 'main', name: c.contractor || '', crm_task: c.crm_task || '', task_id: c.task_id || '' });
        if (c.sub) out.push({ role: 'sub', name: c.sub, crm_task: '', task_id: '' });
      } else if (c.sub) {
        out.push({ role: 'sub', name: c.sub, crm_task: c.crm_task || '', task_id: c.task_id || '' });
      }
    });
    if (!out.length || out[0].role !== 'main') out.unshift({ role: 'main', name: '', crm_task: '', task_id: '' });
    return out;
  };

  const mergeDuplicateProjects = (rows) => {
    const map = new Map(); const result = [];
    rows.forEach(row => {
      const name = (row['Project Name'] || '').trim().toLowerCase();
      const mainName = row['Contractor'] || '';
      const subName = row['Sub Contractor'] || '';
      const crmTask = row['Key Actions for next Meeting'] || '';
      const makeInitial = () => {
        const entries = [{ role: 'main', name: mainName, crm_task: mainName ? crmTask : '', task_id: '' }];
        if (subName) entries.push({ role: 'sub', name: subName, crm_task: !mainName ? crmTask : '', task_id: '' });
        return entries;
      };
      if (!name) { result.push({ ...row, _contractors: makeInitial() }); return; }
      if (map.has(name)) {
        const existing = map.get(name);
        if (mainName) {
          const mainIdx = existing._contractors.findIndex(c => c.role === 'main');
          if (mainIdx >= 0 && !existing._contractors[mainIdx].name) existing._contractors[mainIdx].name = mainName;
        }
        if (subName && !existing._contractors.some(c => c.role === 'sub' && c.name === subName)) {
          existing._contractors.push({ role: 'sub', name: subName, crm_task: !mainName ? crmTask : '', task_id: '' });
        }
        Object.keys(row).forEach(k => { if (row[k] && !existing[k]) existing[k] = row[k]; });
      } else {
        const m = { ...row, _contractors: makeInitial() };
        map.set(name, m); result.push(m);
      }
    });
    return result;
  };

  // ===== ACTIONS PROMPT =====
  const handleMoveActions = (doMove) => {
    if (doMove) {
      setTab({ rows: tab.rows.map(r => ({ ...r, 'Action Review from Last Meeting': r['Key Actions for next Meeting'] || '', 'Key Actions for next Meeting': '' })) });
      toast.success('Next actions moved to last actions review');
    }
    setShowActionsPrompt(false);
  };

  // ===== SDP EDITING =====
  const updateSdpCell = (i, h, v) => { const u = [...tab.rows]; u[i] = { ...u[i], [h]: v }; setTab({ rows: u }); };
  const addSdpContractor = (i) => { const u = [...tab.rows]; u[i]._contractors = [...(u[i]._contractors || []), { role: 'sub', name: '', crm_task: '', task_id: '' }]; setTab({ rows: u }); };
  const updateSdpContractor = (i, ci, f, v) => { const u = [...tab.rows]; u[i]._contractors[ci] = { ...u[i]._contractors[ci], [f]: v }; setTab({ rows: u }); };
  const addSdpBlank = () => { const b = {}; tab.headers.forEach(h => { b[h] = ''; }); b._contractors = [{ role: 'main', name: '', crm_task: '', task_id: '' }]; setTab({ rows: [...tab.rows, b] }); };
  const deleteSdpRow = (i) => setTab({ rows: tab.rows.filter((_, j) => j !== i) });

  // ===== CRM EDITING =====
  const updateCrmField = (i, f, v) => { const u = [...tab.projects]; u[i] = { ...u[i], [f]: v }; setTab({ projects: u }); };
  const updateCrmTrade = (i, ti, f, v) => { const u = [...tab.projects]; u[i].trades[ti] = { ...u[i].trades[ti], [f]: v }; setTab({ projects: u }); };
  const addCrmProject = () => { const trades = tab.trades.map(t => ({ trade: t, sub_contractor: '', status: '', starting: '', value: '' })); setTab({ projects: [...tab.projects, { project_name: '', main_contractor: '', trades }] }); };
  const deleteCrmProject = (i) => setTab({ projects: tab.projects.filter((_, j) => j !== i) });

  // ===== DRAG =====
  const handleDragStart = (i) => setDragIdx(i);
  const handleDragOver = (e, i) => { e.preventDefault(); if (i !== dragOverIdx) setDragOverIdx(i); };
  const handleDrop = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setDragOverIdx(null); return; }
    const key = tab.fileMode === 'sdp' ? 'rows' : 'projects';
    const arr = [...(tab.fileMode === 'sdp' ? tab.rows : tab.projects)];
    const [moved] = arr.splice(dragIdx, 1); arr.splice(i, 0, moved);
    setTab({ [key]: arr }); setDragIdx(null); setDragOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  // ===== CRM QUOTES IMPORT =====
  // Pull a normalized Record ID off a row regardless of header casing.
  const recordIdOf = (r) => String(r?.['Record ID'] || r?.['Record Id'] || '').trim();

  const handleCrmQuotesUpload = async (file) => {
    if (!file) return; setLoadingCrm(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/api/sdp-report/parse-crm`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail);
      // Pre-select any CRM rows whose Record ID is already present in the current
      // SDP tab — lets the user just tick extras (or hit Import to refresh updates).
      const existingIds = new Set((tab.rows || []).map(recordIdOf).filter(Boolean));
      const preselected = {};
      let matchCount = 0;
      d.rows.forEach((r, i) => {
        const rid = recordIdOf(r);
        if (rid && existingIds.has(rid)) { preselected[i] = true; matchCount++; }
      });
      setCrmHeaders(d.headers); setCrmRows(d.rows); setCrmSelected(preselected);
      toast.success(
        matchCount > 0
          ? `Loaded ${d.rows.length} quotes · ${matchCount} already in SDP (pre-selected)`
          : `Loaded ${d.rows.length} quotes`
      );
    } catch (e) { toast.error(e.message); } finally { setLoadingCrm(false); }
  };
  const toggleCrmRow = (i) => setCrmSelected(p => ({ ...p, [i]: !p[i] }));
  const selectAllCrm = () => { const all = Object.keys(crmSelected).length === crmRows.length && Object.values(crmSelected).every(Boolean); setCrmSelected(all ? {} : Object.fromEntries(crmRows.map((_, i) => [i, true]))); };
  const selectedCount = Object.values(crmSelected).filter(Boolean).length;
  const importCrmQuotes = () => {
    const indices = Object.entries(crmSelected).filter(([, v]) => v).map(([k]) => parseInt(k));
    if (!indices.length) { toast.error('No projects selected'); return; }
    // Ensure SDP tab headers include Record ID fields (for older uploads that didn't have them)
    const extraHeaders = ['Record ID', 'Project Record ID', 'Address/Project Address'];
    const newHeaders = [...tab.headers];
    extraHeaders.forEach(h => { if (!newHeaders.includes(h)) newHeaders.push(h); });
    const updated = [...tab.rows]; let added = 0, updatedC = 0;
    indices.forEach(idx => {
      const cr = crmRows[idx]; const mapped = {};
      Object.entries(CRM_TO_MEETING_MAP).forEach(([c, m]) => { if (cr[c] && newHeaders.includes(m)) mapped[m] = cr[c]; });
      if (newHeaders.includes('On Encon CRM Y/N')) mapped['On Encon CRM Y/N'] = 'Y';
      // Match by Record ID first (unique & stable), then fall back to Project Name.
      const crmRid = recordIdOf(cr);
      let ei = crmRid ? updated.findIndex(r => recordIdOf(r) === crmRid) : -1;
      if (ei < 0) {
        const n = (cr['Project Name'] || '').toLowerCase().trim();
        ei = n ? updated.findIndex(r => (r['Project Name'] || '').toLowerCase().trim() === n) : -1;
      }
      if (ei >= 0) { Object.entries(mapped).forEach(([k, v]) => { if (v) updated[ei] = { ...updated[ei], [k]: v }; }); updatedC++; }
      else { const nr = {}; newHeaders.forEach(h => { nr[h] = ''; }); Object.entries(mapped).forEach(([k, v]) => { nr[k] = v; }); nr['Project Name'] = cr['Project Name'] || ''; nr._contractors = []; nr._contractors.push({ role: 'main', name: mapped['Contractor'] || '', crm_task: '', task_id: '' }); if (mapped['Sub Contractor']) nr._contractors.push({ role: 'sub', name: mapped['Sub Contractor'], crm_task: '', task_id: '' }); updated.push(nr); added++; }
    });
    setTab({ headers: newHeaders, rows: updated }); setCrmModalOpen(false);
    toast.success([added && `${added} added`, updatedC && `${updatedC} updated`].filter(Boolean).join(', '));
  };

  // ===== SAVE =====
  const handleSave = async () => {
    if (!tab.loaded) return;
    setSaving(true);
    try {
      const fd = new FormData();
      // File may be null after a page reload — backend will use the stored template bytes
      if (tab.file) fd.append('file', tab.file);
      if (tab.filename) fd.append('filename', tab.filename);
      fd.append('tab_key', activeTab);
      let endpoint;
      if (tab.fileMode === 'sdp') {
        // Each contractor entry becomes its own spreadsheet row.
        const flat = [];
        tab.rows.forEach(r => {
          const base = { ...r };
          delete base._contractors;
          const contractors = (r._contractors && r._contractors.length) ? r._contractors : [{ role: 'main', name: '', crm_task: '' }];
          contractors.forEach((c) => {
            const out = { ...base };
            if (c.role === 'main') {
              out['Contractor'] = c.name || '';
              out['Sub Contractor'] = '';
            } else {
              out['Contractor'] = '';
              out['Sub Contractor'] = c.name || '';
            }
            out['Key Actions for next Meeting'] = c.crm_task || '';
            flat.push(out);
          });
        });
        fd.append('rows_json', JSON.stringify(flat)); endpoint = `${API}/api/sdp-report/generate`;
      } else {
        // CRM mode still requires the original file (template-based generation)
        if (!tab.file) { toast.error('Please re-upload the original CRM file to download updates.'); setSaving(false); return; }
        fd.append('projects_json', JSON.stringify(tab.projects)); endpoint = `${API}/api/crm-report/generate`;
      }
      const res = await fetch(endpoint, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      const blob = await res.blob(); const url = window.URL.createObjectURL(blob); const a = document.createElement('a');
      a.href = url; a.download = res.headers.get('Content-Disposition')?.match(/filename="?(.+?)"?$/)?.[1] || (tab.filename || 'Supplier_Meeting.xlsx').replace('.xlsx', '_updated.xlsx');
      document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
      toast.success('Updated file downloaded');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  // ===== HELPERS =====
  const getShortHeader = (h) => ({ 'On Encon CRM Y/N': 'CRM', 'Sub Contractor Quoted Y/N': 'Qtd', 'Potential Value to Encon': 'Value', 'Action Review from Last Meeting': 'Last Actions', 'Key Actions for next Meeting': 'Next Actions', 'Any other comments': 'Comments', 'Sub Contractor': 'Sub Con', 'Lead / Source': 'Lead' }[h] || h);

  // Format YYYY-MM-DD (or ISO) to DD.MM.YY for display only
  const fmtDdMmYy = (v) => {
    if (!v) return '';
    const s = String(v);
    // Match YYYY-MM-DD at the start (ignoring any trailing time)
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}.${m[2]}.${m[1].slice(2)}`;
    return s;
  };

  const EditableField = ({ value, onChange, label, multiline, editKey, format }) => {
    const [localValue, setLocalValue] = useState(value || '');
    
    // Update local value when prop changes
    useEffect(() => {
      if (editingCell !== editKey) {
        setLocalValue(value || '');
      }
    }, [value, editingCell, editKey]);
    
    const handleChange = (e) => {
      setLocalValue(e.target.value);
    };
    
    const handleBlur = () => {
      onChange(localValue);
      setEditingCell(null);
    };
    
    if (editingCell === editKey) {
      return (<div><label className={fieldLabelClass}>{label}</label>
        <textarea autoFocus className="w-full border rounded-lg px-2 py-1 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300 mt-0.5 bg-white"
          style={{ minHeight: multiline ? '70px' : '32px' }} value={localValue} onChange={handleChange}
          onBlur={handleBlur} onKeyDown={(e) => { if (e.key === 'Escape') { setLocalValue(value || ''); setEditingCell(null); } }} /></div>);
    }
    let displayValue = value;
    if (format === 'date') {
      displayValue = fmtDdMmYy(value);
    } else if (format === 'currency' && value !== '' && value != null) {
      // Extract numeric portion, format with commas and a £ prefix
      const raw = String(value).replace(/[^0-9.\-]/g, '');
      const num = parseFloat(raw);
      if (!Number.isNaN(num)) {
        displayValue = `£${num.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;
      }
    }
    return (<div className={fieldBoxClass} onClick={() => setEditingCell(editKey)} title={value || 'Click to edit'}>
      <label className={fieldLabelClass}>{label}</label>
      <p className={`${fieldTextClass} ${multiline ? 'whitespace-pre-wrap' : ''} ${!displayValue ? 'text-gray-400 italic' : ''}`}>{displayValue || '—'}</p>
    </div>);
  };

  // Create task in Zoho via API (fully linked to Project/Quote), open for editing.
  // Stores the returned task_id on the contractor so an "Edit task" link can reopen it later.
  const openAddToCrmModal = async (recordId, nextAction, type = 'quote', projectName = '', ri = null, ci = null) => {
    if (!recordId) { toast.error('No Record ID available'); return; }
    const module = type === 'project' ? 'Potentials' : 'Quotes';
    const contractorLabel = (ri != null && ci != null && tab.rows?.[ri]?._contractors?.[ci])
      ? (tab.rows[ri]._contractors[ci].name || (tab.rows[ri]._contractors[ci].role === 'main' ? 'Main Contractor' : `Sub Contractor ${ci}`))
      : '';
    const firstLine = (nextAction && nextAction.trim().split('\n')[0].slice(0, 140)) || '';
    const projectPart = projectName ? ` (${projectName})` : '';
    const subject = firstLine
      ? (contractorLabel ? `${contractorLabel}: ${firstLine}${projectPart}` : `${firstLine}${projectPart}`)
      : (contractorLabel ? `${contractorLabel} follow-up${projectPart}` : `SDP follow-up${projectPart}`);
    const loadingToast = toast.loading('Creating task in Zoho CRM…');
    try {
      const res = await fetch(`${API}/api/zoho/create-task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          record_id: recordId,
          module,
          subject,
          description: nextAction || '',
          priority: 'Normal',
          status: 'Not Started',
        }),
      });
      const data = await res.json();
      toast.dismiss(loadingToast);
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to create task in Zoho CRM');
      }
      // Persist task_id on the contractor so we can show "Edit task" next time
      if (ri != null && ci != null) {
        updateSdpContractor(ri, ci, 'task_id', data.task_id);
      }
      window.open(data.task_url, '_blank', 'noopener,noreferrer');
      toast.success(
        `Task opened in Zoho (linked to ${module === 'Potentials' ? 'project' : 'quote'})${contractorLabel ? ' for ' + contractorLabel : ''}.`,
        { duration: 6000 }
      );
    } catch (e) {
      toast.dismiss(loadingToast);
      toast.error(`Zoho CRM: ${e.message}`);
    }
  };

  // ===== PDF DOWNLOAD =====
  const handleDownloadPDF = async () => {
    if (!cardsRef.current) return;
    setPdfLoading(true);
    try {
      const html2canvasLib = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      // Inject style to hide interactive-only elements
      const style = document.createElement('style');
      style.id = 'sdp-pdf-style';
      style.textContent = '[data-pdf-hide]{display:none!important;visibility:hidden!important;}';
      document.head.appendChild(style);

      const cardEls = Array.from(cardsRef.current.querySelectorAll('[data-pdf-card]'));
      if (cardEls.length === 0) { toast.error('No projects to export'); return; }

      const tabLabel = sdpTabs.find(t => t.key === activeTab)?.label || activeTab;
      const date = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
      const filename = `SDP_${tabLabel.replace(/\s+/g, '_')}_${date}.pdf`;

      const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pageW = doc.internal.pageSize.getWidth();   // 210
      const pageH = doc.internal.pageSize.getHeight();  // 297
      const margin = 10;

      const titles = cardEls.map(el =>
        el.querySelector('[data-pdf-title]')?.textContent?.trim() || 'Untitled Project'
      );

      // ── PAGE 1: TABLE OF CONTENTS ──────────────────────────────────────────
      // Orange header
      doc.setFillColor(238, 114, 3);
      doc.rect(0, 0, pageW, 28, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(tabLabel, margin, 17);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated ${date}`, pageW - margin, 17, { align: 'right' });

      // Sub-header
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Contents', margin, 40);

      // Divider
      doc.setDrawColor(238, 114, 3);
      doc.setLineWidth(0.4);
      doc.line(margin, 43, pageW - margin, 43);

      // TOC rows — page 2 = card index 0, page 3 = card index 1, etc.
      const rowH = 10;
      const tocStartY = 50;
      titles.forEach((title, idx) => {
        const y = tocStartY + idx * rowH;
        const pageNum = idx + 2;

        // Alternating row bg
        if (idx % 2 === 0) {
          doc.setFillColor(255, 247, 237);
          doc.rect(margin - 2, y - 6.5, pageW - (margin - 2) * 2, rowH, 'F');
        }

        // Index number
        doc.setTextColor(238, 114, 3);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`${idx + 1}`, margin + 2, y);

        // Title (truncated)
        const maxW = pageW - margin * 2 - 22;
        const truncated = doc.splitTextToSize(title, maxW)[0] +
          (doc.splitTextToSize(title, maxW).length > 1 ? '…' : '');
        doc.setTextColor(30, 30, 30);
        doc.setFont('helvetica', 'normal');
        doc.text(truncated, margin + 10, y);

        // Page number (right-aligned)
        doc.setTextColor(150, 150, 150);
        doc.text(`p.${pageNum}`, pageW - margin, y, { align: 'right' });

        // Clickable link over the entire row
        doc.link(margin - 2, y - 6.5, pageW - (margin - 2) * 2, rowH, { pageNumber: pageNum });
      });

      // Footer note
      doc.setFontSize(7);
      doc.setTextColor(180, 180, 180);
      doc.text('Click a project title to jump directly to its page', margin, pageH - 8);

      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2;

      // ── PAGES 2+: ONE CARD PER PAGE ────────────────────────────────────────
      // A4 portrait at 96 dpi: width = 750 px, height = 750 × 297/210 ≈ 1063 px
      const A4_W = 750;
      const A4_H = Math.round(A4_W * 297 / 210); // 1063 px — same ratio as availW/availH

      // Inject an extra style that:
      //  • removes overflow:hidden from h3 titles so descenders aren't clipped
      //  • ensures each card's background fills the full A4 height
      const captureStyle = document.createElement('style');
      captureStyle.id = 'sdp-pdf-capture-style';
      captureStyle.textContent = `
        [data-pdf-card] h3[data-pdf-title] { overflow: visible !important; white-space: normal !important; }
        [data-pdf-card] { background-color: #f8f8f8; }
      `;
      document.head.appendChild(captureStyle);

      for (let i = 0; i < cardEls.length; i++) {
        doc.addPage();

        const el = cardEls[i];
        const prevStyle = el.getAttribute('style') || '';

        // Force every card to exactly A4 portrait dimensions so all pages
        // are uniform. minHeight fills short cards; the capture height
        // argument clips content beyond one page (keeps tall cards readable).
        el.style.cssText = `
          width: ${A4_W}px !important;
          max-width: ${A4_W}px !important;
          min-height: ${A4_H}px;
          box-sizing: border-box;
          position: relative;
        `;

        // Brief pause so the browser can reflow the card at the new width.
        await new Promise(r => setTimeout(r, 40));

        const canvas = await html2canvasLib(el, {
          scale: 1.5,
          useCORS: true,
          logging: false,
          backgroundColor: '#f8f8f8',
          width: A4_W,
          height: A4_H,        // capture exactly one A4 page — clips below the fold
          windowWidth: A4_W + 20,
        });

        // Restore original style
        el.setAttribute('style', prevStyle);

        // Place image to fill the full usable page area exactly —
        // A4_W/A4_H ratio == availW/availH so no distortion.
        doc.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, availW, availH);

        // Small back-to-TOC link at bottom
        doc.setFontSize(7);
        doc.setTextColor(180, 180, 180);
        doc.text('↑ Back to contents', margin, pageH - 6);
        doc.link(margin, pageH - 10, 35, 6, { pageNumber: 1 });

        // Page number
        doc.text(`${i + 1} / ${cardEls.length}`, pageW - margin, pageH - 6, { align: 'right' });
      }

      // Remove the capture-only style overrides
      const cs = document.getElementById('sdp-pdf-capture-style');
      if (cs) cs.remove();

      doc.save(filename);
    } catch (e) {
      toast.error('Failed to generate PDF');
      console.error(e);
    } finally {
      const s = document.getElementById('sdp-pdf-style');
      if (s) s.remove();
      setPdfLoading(false);
    }
  };

  const dataItems = tab.fileMode === 'sdp' ? tab.rows : tab.projects;

  return (
    <div className="min-h-screen" style={{ backgroundColor: enconLightGrey }}>
      <AppHeader
        user={user}
        onLogout={onLogout}
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Supplier Development Plans</span>
          </div>
        }
      />

      <div className="px-4 lg:px-6 pb-12 pt-[20px] bg-gray-100 min-h-screen">
        <div className="max-w-[1800px] mx-auto">
          {/* Nav Pills */}
          

          {/* Page Tabs */}
          <div className="flex items-center gap-1 mt-4 mb-4 border-b border-gray-200">
            {/* SDP tabs */}
            {sdpTabs.map(t => (
              <div key={t.key} className="relative group">
                <button onClick={() => { setActiveTab(t.key); setEditingCell(null); setShowActionsPrompt(false); }}
                  className={`px-6 py-2.5 text-sm font-semibold rounded-t-xl transition-all ${activeTab === t.key ? 'text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                  style={activeTab === t.key ? { backgroundColor: enconOrange } : {}}
                  data-testid={`tab-${t.key}`}
                >{t.label}</button>
                {sdpTabs.length > 1 && (
                  <button onClick={(e) => { e.stopPropagation(); removeSdpTab(t.key); }}
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gray-400 text-white text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                    title={`Remove ${t.label}`}>x</button>
                )}
              </div>
            ))}

            {/* Add SDP tab */}
            {addingTab ? (
              <div className="flex items-center gap-1 px-2">
                <input autoFocus className="border rounded-lg px-2 py-1.5 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-orange-300"
                  placeholder="Supplier name..." value={newTabName} onChange={(e) => setNewTabName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addSdpTab(newTabName); if (e.key === 'Escape') { setAddingTab(false); setNewTabName(''); } }}
                  onBlur={() => { if (!newTabName.trim()) { setAddingTab(false); setNewTabName(''); } }}
                />
                <button onClick={() => addSdpTab(newTabName)} className="text-xs font-medium px-2 py-1.5 rounded-lg text-white" style={{ backgroundColor: enconOrange }}>Add</button>
              </div>
            ) : (
              <button onClick={() => setAddingTab(true)} className="px-3 py-2.5 text-sm text-gray-400 hover:text-orange-500 transition-colors" title="Add supplier SDP tab" data-testid="add-sdp-tab">
                <Plus className="w-4 h-4 inline mr-1" />Add SDP
              </button>
            )}

            {/* Divider */}
            <div className="w-px h-8 bg-gray-300 mx-2 self-end mb-1"></div>

            {/* Key Projects tab (always present) */}
            <button onClick={() => { setActiveTab('key-projects'); setEditingCell(null); setShowActionsPrompt(false); }}
              className={`px-6 py-2.5 text-sm font-semibold rounded-t-xl transition-all ${activeTab === 'key-projects' ? 'text-white' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              style={activeTab === 'key-projects' ? { backgroundColor: '#2563eb' } : {}}
              data-testid="tab-key-projects"
            >Key Projects</button>
          </div>

          {/* Toolbar */}
          {tab.loaded && (
            <div className={`flex items-center px-4 py-2 bg-white rounded-xl border mb-4 shadow-sm ${isKeyProjects ? 'border-blue-200' : ''}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-bold" style={{ color: isKeyProjects ? '#2563eb' : enconOrange }}>{tab.fileMode === 'crm' ? 'Key Projects' : 'SDP Report'}</span>
                {tab.filename && <span className="text-xs text-gray-400 truncate max-w-[200px]">{tab.filename}</span>}
              </div>
              <div className="flex-1 flex justify-center">
                <Button onClick={tab.fileMode === 'sdp' ? addSdpBlank : addCrmProject} variant="outline" className="rounded-xl text-xs h-8 px-3" data-testid="add-row-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" />Add
                </Button>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex rounded-xl overflow-hidden border">
                  <button onClick={() => setViewMode('list')} className={`flex items-center gap-1 px-2.5 h-8 text-xs font-medium transition-colors ${viewMode === 'list' ? 'bg-gray-700 text-white' : 'bg-white text-gray-500 hover:bg-gray-100'}`}><List className="w-3.5 h-3.5" />List</button>
                  <button onClick={() => setViewMode('cards')} className={`flex items-center gap-1 px-2.5 h-8 text-xs font-medium transition-colors ${viewMode === 'cards' ? 'bg-gray-700 text-white' : 'bg-white text-gray-500 hover:bg-gray-100'}`}><LayoutGrid className="w-3.5 h-3.5" />Cards</button>
                </div>
                {tab.fileMode === 'sdp' && (
                  <Button onClick={() => { setCrmHeaders([]); setCrmRows([]); setCrmSelected({}); setCrmModalOpen(true); }}
                    className="text-white font-medium rounded-xl text-xs h-8 px-3" style={{ backgroundColor: '#2563eb' }} data-testid="update-crm-btn"><Upload className="w-3.5 h-3.5 mr-1.5" />Update CRM</Button>
                )}
                <Button onClick={handleSave} disabled={saving} className="text-white font-medium rounded-xl text-xs h-8 px-3" style={{ backgroundColor: isKeyProjects ? '#2563eb' : enconOrange }} data-testid="save-download-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}Save & Download
                </Button>
                {tab.fileMode === 'sdp' && viewMode === 'cards' && (
                  <Button onClick={handleDownloadPDF} disabled={pdfLoading || saving} className="text-white font-medium rounded-xl text-xs h-8 px-3" style={{ backgroundColor: '#dc2626' }} data-testid="pdf-download-btn" title="Download PDF of current card view">
                    {pdfLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}PDF
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Actions prompt */}
          {showActionsPrompt && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between mb-4" data-testid="actions-prompt">
              <p className="text-sm text-blue-800 font-medium">Move <strong>Next Actions</strong> into <strong>Last Actions Review</strong>?</p>
              <div className="flex items-center gap-2 ml-4">
                <Button onClick={() => handleMoveActions(true)} className="text-white font-medium rounded-xl text-xs h-8 px-3" style={{ backgroundColor: enconOrange }}>Yes, move</Button>
                <Button variant="outline" onClick={() => handleMoveActions(false)} className="rounded-xl text-xs h-8 px-3">No, keep</Button>
              </div>
            </div>
          )}

          {/* Content */}
          {!tab.loaded ? (
            /* Upload zone */
            <div className="flex flex-col items-center justify-center py-24"
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) handleFileUpload(e.dataTransfer.files[0]); }}
            >
              <div className={`border-2 border-dashed rounded-2xl p-16 text-center cursor-pointer transition-all ${dragging ? 'border-orange-500 bg-orange-50' : 'hover:border-orange-400 hover:bg-white'}`}
                style={{ borderColor: dragging ? undefined : '#ddd' }} onClick={() => !loadingFile && fileInputRef.current?.click()}>
                {loadingFile ? <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: enconOrange }} /> : <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-gray-300" />}
                <p className="text-lg font-semibold text-gray-700 mb-2">{loadingFile ? 'Detecting file type...' : isKeyProjects ? 'Upload Commercial Review file' : 'Upload Supplier Meeting file'}</p>
                <p className="text-sm text-gray-400">Drag and drop or click to browse (.xlsx)</p>
              </div>
              <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => { if (e.target.files[0]) handleFileUpload(e.target.files[0]); e.target.value = ''; }} />
            </div>
          ) : viewMode === 'list' ? (
            /* LIST VIEW */
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="overflow-auto max-h-[calc(100vh-320px)]">
                {tab.fileMode === 'sdp' ? (
                  <table className="w-full text-xs border-collapse" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '30px' }} />
                      {tab.headers.map(h => { const hl = h.toLowerCase(); let w = '6%'; if (hl.includes('action') || hl.includes('comment')) w = '12%'; else if (hl.includes('project name')) w = '12%'; else if (hl.includes('sub contractor') && !hl.includes('quoted')) w = '7%'; else if (hl.includes('contractor')) w = '8%'; else if (hl.includes('lead') || hl.includes('source')) w = '4%'; else if (hl.includes('y/n') || hl.includes('quoted')) w = '3.5%'; else if (hl.includes('date')) w = '5.5%'; else if (hl.includes('value') || hl.includes('total')) w = '5%'; else if (hl.includes('crm')) w = '3.5%'; return <col key={h} style={{ width: w }} />; })}
                      <col style={{ width: '28px' }} />
                    </colgroup>
                    <thead><tr className="bg-gray-700 text-white sticky top-0 z-10">
                      <th className="px-1 py-2 border-r border-gray-600"></th>
                      {tab.headers.map(h => <th key={h} className="px-1 py-2 text-left font-medium text-[10px] uppercase tracking-wider border-r border-gray-600 truncate" title={h}>{getShortHeader(h)}</th>)}
                      <th className="px-1 py-2"></th>
                    </tr></thead>
                    <tbody>{tab.rows.map((row, i) => (
                      <tr key={i} draggable onDragStart={() => handleDragStart(i)} onDragOver={(e) => handleDragOver(e, i)} onDrop={() => handleDrop(i)} onDragEnd={handleDragEnd}
                        className={`border-b transition-colors ${dragIdx === i ? 'opacity-40' : ''} ${dragOverIdx === i && dragIdx !== null && dragIdx !== i ? 'border-t-2 border-t-orange-400' : ''} ${i % 2 === 0 ? 'bg-white hover:bg-orange-50/40' : 'bg-gray-50/50 hover:bg-orange-50/40'}`}>
                        <td className="px-1 py-1.5 text-center border-r border-gray-200 cursor-grab"><GripVertical className="w-3 h-3 text-gray-300 mx-auto" /></td>
                        {tab.headers.map(h => (
                          <td key={h} className="px-1 py-1 border-r border-gray-200 overflow-hidden">
                            {editingCell === `l-${i}-${h}` ? (
                              <textarea autoFocus className="w-full border rounded px-1 py-0.5 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-orange-300" style={{ minHeight: '50px' }}
                                value={row[h] || ''} onChange={(e) => updateSdpCell(i, h, e.target.value)} onBlur={() => setEditingCell(null)} onKeyDown={(e) => { if (e.key === 'Escape') setEditingCell(null); }} />
                            ) : (
                              <div className="px-0.5 py-1 rounded cursor-pointer hover:bg-orange-100/50 min-h-[22px] text-xs truncate" onClick={() => setEditingCell(`l-${i}-${h}`)} title={row[h] || ''}>{row[h] || <span className="text-gray-300">—</span>}</div>
                            )}
                          </td>
                        ))}
                        <td className="px-1 py-1 text-center"><button onClick={() => deleteSdpRow(i)} className="text-gray-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : (
                  <table className="w-full text-xs border-collapse" style={{ tableLayout: 'fixed' }}>
                    <colgroup><col style={{ width: '30px' }} /><col style={{ width: '18%' }} /><col style={{ width: '15%' }} />
                      {tab.trades.map(t => <col key={t} style={{ width: `${55 / tab.trades.length}%` }} />)}<col style={{ width: '28px' }} /></colgroup>
                    <thead><tr className="bg-gray-700 text-white sticky top-0 z-10">
                      <th className="px-1 py-2 border-r border-gray-600"></th><th className="px-1 py-2 text-left text-[10px] uppercase border-r border-gray-600">Project</th><th className="px-1 py-2 text-left text-[10px] uppercase border-r border-gray-600">Main Contractor</th>
                      {tab.trades.map(t => <th key={t} className="px-1 py-2 text-left text-[10px] uppercase border-r border-gray-600">{t}</th>)}<th className="px-1 py-2"></th>
                    </tr></thead>
                    <tbody>{tab.projects.map((proj, i) => (
                      <tr key={i} draggable onDragStart={() => handleDragStart(i)} onDragOver={(e) => handleDragOver(e, i)} onDrop={() => handleDrop(i)} onDragEnd={handleDragEnd}
                        className={`border-b ${dragIdx === i ? 'opacity-40' : ''} ${i % 2 === 0 ? 'bg-white hover:bg-orange-50/40' : 'bg-gray-50/50 hover:bg-orange-50/40'}`}>
                        <td className="px-1 py-1.5 text-center border-r border-gray-200 cursor-grab"><GripVertical className="w-3 h-3 text-gray-300 mx-auto" /></td>
                        <td className="px-1 py-1 border-r border-gray-200 overflow-hidden"><div className="text-xs truncate">{proj.project_name || '—'}</div></td>
                        <td className="px-1 py-1 border-r border-gray-200 overflow-hidden"><div className="text-xs truncate">{proj.main_contractor || '—'}</div></td>
                        {tab.trades.map((t, ti) => { const trade = proj.trades?.[ti] || {}; return <td key={t} className="px-1 py-1 border-r border-gray-200 overflow-hidden"><div className="text-xs truncate">{trade.sub_contractor || '—'}</div></td>; })}
                        <td className="px-1 py-1 text-center"><button onClick={() => deleteCrmProject(i)} className="text-gray-300 hover:text-red-500"><Trash2 className="w-3 h-3" /></button></td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            /* CARD VIEW */
            <div ref={cardsRef} className="grid grid-cols-2 gap-4">
              {dataItems.length === 0 ? <div className="col-span-2 text-center py-16 text-gray-400">No projects. Click Add or upload a file.</div> : null}
              {tab.fileMode === 'sdp' ? tab.rows.map((row, ri) => {
                const projectAddress = row['Address/Project Address'] || row['Project Address'] || row['Address'] || '';
                const quoteIdRaw = row['Record ID'] || row['Record Id'] || '';
                const projectIdRaw = row['Project Record ID'] || '';
                const quoteIdClean = quoteIdRaw.replace('zcrm_', '');
                const projectIdClean = projectIdRaw.replace('zcrm_', '');
                const quoteUrl = quoteIdClean ? `https://crm.zoho.eu/crm/encon/tab/Quotes/${quoteIdClean}` : null;
                const projectUrl = projectIdClean ? `https://crm.zoho.eu/crm/encon/tab/Potentials/${projectIdClean}` : null;
                return (
                <div key={ri} className="rounded-2xl overflow-hidden" draggable onDragStart={() => handleDragStart(ri)} onDragOver={(e) => handleDragOver(e, ri)} onDrop={() => handleDrop(ri)} onDragEnd={handleDragEnd}
                  style={{ border: `2px solid ${enconOrange}`, boxShadow: '0 4px 16px rgba(238,114,3,0.12), 0 1px 4px rgba(0,0,0,0.06)' }} data-testid={`project-card-${ri}`} data-pdf-card="true">
                  <div className="px-4 py-3" style={{ backgroundColor: enconOrange }}>
                    <div className="flex items-center gap-2.5">
                      <GripVertical className="w-4 h-4 text-white/60 cursor-grab flex-shrink-0" data-pdf-hide="true" />
                      {editingCell === `t-${ri}` ? (<input autoFocus className="flex-1 bg-white/90 rounded-lg px-2.5 py-1.5 text-lg font-bold focus:outline-none" value={row['Project Name'] || ''} onChange={(e) => updateSdpCell(ri, 'Project Name', e.target.value)} onBlur={() => setEditingCell(null)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingCell(null); }} />)
                      : (<h3 className="flex-1 text-white font-bold text-lg tracking-tight truncate cursor-pointer hover:text-white/90" onClick={() => setEditingCell(`t-${ri}`)} data-pdf-title="true">{row['Project Name'] || 'Untitled Project'}</h3>)}
                      <button onClick={() => deleteSdpRow(ri)} className="text-white/60 hover:text-white flex-shrink-0" data-testid={`delete-row-${ri}`} data-pdf-hide="true"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    {editingCell === `addr-${ri}` ? (
                      <input autoFocus className="w-full bg-white/90 rounded-lg px-2.5 py-1 text-sm mt-1.5 ml-6 focus:outline-none" style={{ width: 'calc(100% - 1.5rem)' }}
                        value={projectAddress}
                        onChange={(e) => updateSdpCell(ri, row['Address/Project Address'] !== undefined ? 'Address/Project Address' : (row['Project Address'] !== undefined ? 'Project Address' : 'Address/Project Address'), e.target.value)}
                        onBlur={() => setEditingCell(null)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingCell(null); }} />
                    ) : (
                      <div className="text-white/90 text-sm font-medium mt-1 ml-6 truncate cursor-pointer hover:text-white" onClick={() => setEditingCell(`addr-${ri}`)} data-testid={`project-address-${ri}`}>
                        {projectAddress || 'Add project address…'}
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-white space-y-3">
                    {(quoteUrl || projectUrl) && (
                      <div className="flex justify-end gap-2 -mt-1 flex-wrap items-center text-xs font-medium" data-pdf-hide="true">
                        {quoteUrl && (
                          <a href={quoteUrl} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                            data-testid={`view-quote-in-crm-${ri}`}>
                            <ExternalLink className="w-3 h-3" />
                            View Quote in CRM
                          </a>
                        )}
                        {quoteUrl && projectUrl && <span className="text-gray-300 select-none">‖</span>}
                        {projectUrl && (
                          <a href={projectUrl} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-800 hover:underline"
                            data-testid={`view-project-in-crm-${ri}`}>
                            <ExternalLink className="w-3 h-3" />
                            View Project in CRM
                          </a>
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-6 gap-2">
                      <EditableField value={row['Lead / Source']} onChange={v => updateSdpCell(ri, 'Lead / Source', v)} label="Lead" editKey={`s-${ri}-lead`} />
                      <EditableField value={row['On Encon CRM Y/N']} onChange={v => updateSdpCell(ri, 'On Encon CRM Y/N', v)} label="CRM" editKey={`s-${ri}-crm`} />
                      <EditableField value={row['Potential Value to Encon']} onChange={v => updateSdpCell(ri, 'Potential Value to Encon', v)} label="Value" editKey={`s-${ri}-val`} format="currency" />
                      <EditableField value={row['Start Date']} onChange={v => updateSdpCell(ri, 'Start Date', v)} label="Start" editKey={`s-${ri}-st`} format="date" />
                      <EditableField value={row['End Date']} onChange={v => updateSdpCell(ri, 'End Date', v)} label="End" editKey={`s-${ri}-end`} format="date" />
                      <EditableField value={row['Sub Contractor Quoted Y/N']} onChange={v => updateSdpCell(ri, 'Sub Contractor Quoted Y/N', v)} label="Quoted" editKey={`s-${ri}-q`} />
                    </div>

                    {/* Product Detail (col P) + Success (col Q) — Success
                        sits inline with the Product Detail label; the larger
                        multi-line text box renders beneath. */}
                    <div className={`${fieldBoxClass}`} onClick={(e) => { if (e.target === e.currentTarget) setEditingCell(`s-${ri}-pd`); }}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <label className={fieldLabelClass}>Product Detail</label>
                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <span className={fieldLabelClass}>Success</span>
                          {editingCell === `s-${ri}-success` ? (
                            <input
                              autoFocus
                              className="w-32 border rounded px-2 py-0.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-300"
                              value={row['Success'] || ''}
                              onChange={(e) => updateSdpCell(ri, 'Success', e.target.value)}
                              onBlur={() => setEditingCell(null)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingCell(null); }}
                              placeholder="e.g. 40% / Likely"
                              data-testid={`success-input-${ri}`}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => setEditingCell(`s-${ri}-success`)}
                              className={`min-w-[80px] text-xs px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-orange-50 hover:border-orange-300 text-left ${row['Success'] ? 'text-gray-800 font-medium' : 'text-gray-400 italic'}`}
                              title={row['Success'] || 'Click to add success likelihood'}
                              data-testid={`success-btn-${ri}`}
                            >
                              {row['Success'] || 'add…'}
                            </button>
                          )}
                        </div>
                      </div>
                      {editingCell === `s-${ri}-pd` ? (
                        <textarea
                          autoFocus
                          className="w-full border rounded-lg px-2 py-1 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
                          style={{ minHeight: '70px' }}
                          value={row['Product Detail'] || ''}
                          onChange={(e) => updateSdpCell(ri, 'Product Detail', e.target.value)}
                          onBlur={() => setEditingCell(null)}
                          onKeyDown={(e) => { if (e.key === 'Escape') setEditingCell(null); }}
                          data-testid={`product-detail-input-${ri}`}
                        />
                      ) : (
                        <p
                          className={`${fieldTextClass} whitespace-pre-wrap ${!row['Product Detail'] ? 'text-gray-400 italic' : ''}`}
                          onClick={() => setEditingCell(`s-${ri}-pd`)}
                          data-testid={`product-detail-display-${ri}`}
                        >
                          {row['Product Detail'] || '—'}
                        </p>
                      )}
                    </div>

                    {/* Contractor blocks: each one (main or sub) has name and CRM task side-by-side */}
                    <div className="border-t pt-3 space-y-3">
                      {(row._contractors && row._contractors.length ? row._contractors : [{ role: 'main', name: '', crm_task: '', task_id: '' }]).map((c, ci) => {
                        const isMain = c.role === 'main';
                        const displayName = c.name || (isMain ? 'Main Contractor' : `Sub Contractor ${ci}`);
                        const taskId = c.task_id || '';
                        return (
                          <div key={ci} className="rounded-xl border border-gray-200 bg-gray-50/60 p-2.5 space-y-2">
                            <div className="grid grid-cols-2 gap-3 items-start">
                              <div className="min-w-0">
                                <div className="flex items-end gap-1">
                                  <div className="flex-1 min-w-0">
                                    <EditableField value={c.name} onChange={v => updateSdpContractor(ri, ci, 'name', v)} label={isMain ? 'Main Contractor' : `Sub Contractor ${ci}`} editKey={`s-${ri}-c${ci}`} />
                                  </div>
                                  {!isMain && (
                                    <button
                                      onClick={() => { const u = [...tab.rows]; u[ri]._contractors = u[ri]._contractors.filter((_, j) => j !== ci); setTab({ rows: u }); }}
                                      className="text-gray-400 hover:text-red-500 pb-1 flex-shrink-0"
                                      title="Remove sub-contractor"
                                      data-testid={`remove-sub-contractor-${ri}-${ci}`}
                                      data-pdf-hide="true"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="min-w-0">
                                <EditableField value={c.crm_task || ''} onChange={v => updateSdpContractor(ri, ci, 'crm_task', v)} label={`CRM Task — ${displayName}`} multiline editKey={`s-${ri}-ct${ci}`} />
                              </div>
                            </div>
                            <div className="flex justify-end gap-3 items-center text-xs font-medium" data-pdf-hide="true">
                              {(row['Project Record ID'] || row['Record ID'] || row['Record Id']) && (
                                <button
                                  onClick={() => {
                                    const hasProject = !!row['Project Record ID'];
                                    openAddToCrmModal(
                                      hasProject ? row['Project Record ID'] : (row['Record ID'] || row['Record Id']),
                                      c.crm_task || '',
                                      hasProject ? 'project' : 'quote',
                                      row['Project Name'] || '',
                                      ri, ci
                                    );
                                  }}
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                                  data-testid={`add-task-to-crm-${ri}-${ci}`}
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Add task to crm
                                </button>
                              )}
                              {taskId && (
                                <a href={`https://crm.zoho.eu/crm/encon/tab/Tasks/${taskId}`} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-800 hover:underline"
                                  data-testid={`edit-task-${ri}-${ci}`}>
                                  <ExternalLink className="w-3 h-3" />
                                  Edit task
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <button
                        onClick={() => addSdpContractor(ri)}
                        className="text-xs text-orange-500 hover:text-orange-700 font-medium flex items-center gap-1 mt-1"
                        data-testid={`add-sub-contractor-${ri}`}
                        data-pdf-hide="true"
                      >
                        <Plus className="w-3 h-3" />Add sub-contractor
                      </button>
                    </div>

                    {/* Previous Actions / Comments (info only — passes through to the spreadsheet) */}
                    <div className="border-t pt-3 grid grid-cols-2 gap-3">
                      <EditableField value={row['Action Review from Last Meeting']} onChange={v => updateSdpCell(ri, 'Action Review from Last Meeting', v)} label="Previous Actions" multiline editKey={`s-${ri}-la`} />
                      <EditableField value={row['Any other comments'] || ''} onChange={v => updateSdpCell(ri, 'Any other comments', v)} label="Comments" multiline editKey={`s-${ri}-cm`} />
                    </div>

                    {/* Slim Record IDs bar (bottom) */}
                    <div className="border-t pt-2 flex items-center gap-4 text-[11px]">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Quote ID</span>
                        {editingCell === `s-${ri}-rid` ? (
                          <input autoFocus className="flex-1 min-w-0 border rounded px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-orange-300"
                            defaultValue={row['Record ID'] || row['Record Id'] || ''}
                            onBlur={(e) => { updateSdpCell(ri, row['Record Id'] !== undefined ? 'Record Id' : 'Record ID', e.target.value); setEditingCell(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } if (e.key === 'Escape') setEditingCell(null); }} />
                        ) : (
                          <span onClick={() => setEditingCell(`s-${ri}-rid`)} className="text-gray-700 hover:text-orange-600 cursor-pointer truncate" title="Click to edit">
                            {row['Record ID'] || row['Record Id'] || <em className="text-gray-400">—</em>}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">Project ID</span>
                        {editingCell === `s-${ri}-prid` ? (
                          <input autoFocus className="flex-1 min-w-0 border rounded px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-orange-300"
                            defaultValue={row['Project Record ID'] || ''}
                            onBlur={(e) => { updateSdpCell(ri, 'Project Record ID', e.target.value); setEditingCell(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } if (e.key === 'Escape') setEditingCell(null); }} />
                        ) : (
                          <span onClick={() => setEditingCell(`s-${ri}-prid`)} className="text-gray-700 hover:text-orange-600 cursor-pointer truncate" title="Click to edit">
                            {row['Project Record ID'] || <em className="text-gray-400">—</em>}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                );
              }) : tab.projects.map((proj, pi) => (
                <div key={pi} className="rounded-2xl overflow-hidden" draggable onDragStart={() => handleDragStart(pi)} onDragOver={(e) => handleDragOver(e, pi)} onDrop={() => handleDrop(pi)} onDragEnd={handleDragEnd}
                  style={{ border: '2px solid #2563eb', boxShadow: '0 4px 16px rgba(37,99,235,0.12), 0 1px 4px rgba(0,0,0,0.06)' }} data-testid={`project-card-${pi}`}>
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-600">
                    <GripVertical className="w-4 h-4 text-white/60 cursor-grab flex-shrink-0" />
                    {editingCell === `ct-${pi}` ? (<input autoFocus className="flex-1 bg-white/90 rounded-lg px-2 py-1 text-sm font-bold focus:outline-none" value={proj.project_name || ''} onChange={(e) => updateCrmField(pi, 'project_name', e.target.value)} onBlur={() => setEditingCell(null)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingCell(null); }} />)
                    : (<h3 className="flex-1 text-white font-bold text-sm truncate cursor-pointer hover:text-white/80" onClick={() => setEditingCell(`ct-${pi}`)}>{proj.project_name || 'Untitled Project'}</h3>)}
                    <button onClick={() => deleteCrmProject(pi)} className="text-white/50 hover:text-white flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="p-4 bg-white space-y-3">
                    <EditableField value={proj.main_contractor} onChange={v => updateCrmField(pi, 'main_contractor', v)} label="Main Contractor" editKey={`c-${pi}-mc`} />
                    <div className="border-t pt-3 space-y-2">
                      {(proj.trades || []).map((trade, ti) => (<div key={ti}>
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">{trade.trade}</p>
                        <div className="grid grid-cols-4 gap-2">
                          <EditableField value={trade.sub_contractor} onChange={v => updateCrmTrade(pi, ti, 'sub_contractor', v)} label="Sub Con" editKey={`c-${pi}-t${ti}-s`} />
                          <EditableField value={trade.status} onChange={v => updateCrmTrade(pi, ti, 'status', v)} label="Status" editKey={`c-${pi}-t${ti}-st`} />
                          <EditableField value={trade.starting} onChange={v => updateCrmTrade(pi, ti, 'starting', v)} label="Starting" editKey={`c-${pi}-t${ti}-sr`} />
                          <EditableField value={trade.value} onChange={v => updateCrmTrade(pi, ti, 'value', v)} label="Value" editKey={`c-${pi}-t${ti}-v`} />
                        </div>
                      </div>))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* CRM Quotes Import Modal */}
      <Dialog open={crmModalOpen} onOpenChange={setCrmModalOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[88vh] p-0 overflow-hidden" data-testid="crm-modal"
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.name.endsWith('.xlsx') && crmRows.length === 0) handleCrmQuotesUpload(f); }}>
          <div className="flex flex-col h-[85vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 pr-14">
              <div className="flex items-center gap-3 min-w-0 flex-1 mr-4">
                <button onClick={() => setCrmModalOpen(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><ArrowLeft className="w-5 h-5" /></button>
                <div><DialogTitle className="text-lg font-bold text-blue-700">CRM Details — Select Projects to Import</DialogTitle>
                  <p className="text-sm text-gray-500 mt-0.5">{crmRows.length > 0 ? `${crmRows.length} quotes · ${selectedCount} selected` : 'Upload Supplier Quotes Excel'}</p></div>
              </div>
              {crmRows.length > 0 && (<Button onClick={importCrmQuotes} disabled={selectedCount === 0} className="text-white font-medium rounded-xl text-sm flex-shrink-0" style={{ backgroundColor: selectedCount > 0 ? enconOrange : '#ccc' }} data-testid="import-crm-btn"><Download className="w-4 h-4 mr-2" />Import {selectedCount}</Button>)}
            </div>
            <div className="flex-1 overflow-auto p-6">
              {crmRows.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div onDragOver={(e) => { e.preventDefault(); setCrmDragging(true); }} onDragLeave={() => setCrmDragging(false)} onDrop={(e) => { e.preventDefault(); setCrmDragging(false); if (e.dataTransfer.files[0]) handleCrmQuotesUpload(e.dataTransfer.files[0]); }}
                    className={`border-2 border-dashed rounded-2xl p-16 text-center cursor-pointer transition-all ${crmDragging ? 'border-blue-500 bg-blue-50' : 'hover:border-blue-400 hover:bg-blue-50/30'}`} style={{ borderColor: crmDragging ? undefined : '#ddd' }}
                    onClick={() => !loadingCrm && crmInputRef.current?.click()} data-testid="crm-upload-zone">
                    {loadingCrm ? <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin text-blue-500" /> : <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-gray-300" />}
                    <p className="text-lg font-semibold text-gray-700 mb-2">{loadingCrm ? 'Parsing...' : 'Upload Supplier Quotes (CRM)'}</p>
                    <p className="text-sm text-gray-400">Drag and drop or click to browse</p>
                  </div>
                  <input ref={crmInputRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => { if (e.target.files[0]) handleCrmQuotesUpload(e.target.files[0]); e.target.value = ''; }} />
                </div>
              ) : (() => {
                const hideCols = new Set(['Total', 'Description']);
                const vh = crmHeaders.filter(h => !hideCols.has(h));
                return (<div className="overflow-hidden border rounded-xl"><div className="overflow-auto max-h-[70vh]">
                  <table className="w-full text-sm" style={{ tableLayout: 'fixed' }} data-testid="crm-table">
                    <colgroup><col style={{ width: '40px' }} />{vh.map(h => { const hl = h.toLowerCase(); let w = '11%'; if (hl.includes('project name')) w = '16%'; else if (hl.includes('account name')) w = '16%'; else if (hl.includes('subject')) w = '14%'; else if (hl.includes('grand')) w = '8%'; else if (hl.includes('date')) w = '9%'; return <col key={h} style={{ width: w }} />; })}</colgroup>
                    <thead><tr className="bg-blue-700 text-white sticky top-0 z-10">
                      <th className="px-2 py-3 text-center"><button onClick={selectAllCrm} className="hover:opacity-80" data-testid="select-all-crm">{Object.keys(crmSelected).length === crmRows.length && Object.values(crmSelected).every(Boolean) ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}</button></th>
                      {vh.map(h => <th key={h} className="px-2 py-3 text-left font-medium text-xs uppercase tracking-wider">{h}</th>)}
                    </tr></thead>
                    <tbody>{crmRows.map((row, idx) => (
                      <tr key={idx} onClick={() => toggleCrmRow(idx)} className={`border-b cursor-pointer transition-colors ${crmSelected[idx] ? 'bg-orange-50 hover:bg-orange-100' : idx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-100'}`} data-testid={`crm-row-${idx}`}>
                        <td className="px-2 py-2 text-center">{crmSelected[idx] ? <CheckSquare className="w-5 h-5 inline" style={{ color: enconOrange }} /> : <Square className="w-5 h-5 inline text-gray-300" />}</td>
                        {vh.map(h => <td key={h} className="px-2 py-2 overflow-hidden"><span className="text-sm break-words line-clamp-2 block">{row[h] || ''}</span></td>)}
                      </tr>
                    ))}</tbody>
                  </table>
                </div></div>);
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SDPCRMPage;
